<template>
  <div class="photopea-container" ref="container">
    <iframe 
      ref="photopeaFrame" 
      id="photopea-frame"
      class="photopea-frame"
      :src="photopeaUrl"
      allowfullscreen
    ></iframe>
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PhotopeaEditor',
  props: {
    images: {
      type: Array,
      required: true
    },
    actionFile: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      photopeaUrl: 'https://www.photopea.com',
      photopeaReady: false,
      imagesLoaded: false,
      actionLoaded: false,
      photopeaInstance: null
    };
  },
  watch: {
    images: {
      handler() {
        if (this.photopeaReady && this.images.length > 0) {
          this.loadImages();
        }
      },
      deep: true
    },
    actionFile: {
      handler() {
        if (this.photopeaReady && this.actionFile) {
          this.loadActionFile();
        }
      }
    }
  },
  mounted() {
    // 监听Photopea的消息
    window.addEventListener('message', this.handlePhotopeaMessage);
    
    // 获取iframe引用
    this.photopeaInstance = this.$refs.photopeaFrame.contentWindow;
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handlePhotopeaMessage);
  },
  methods: {
    // 处理来自Photopea的消息
    handlePhotopeaMessage(event) {
      // 忽略非Photopea来源的消息
      if (!event.source.location.href.includes('photopea.com')) return;
      
      const message = event.data;
      
      // 如果Photopea已准备好
      if (message === 'done') {
        this.photopeaReady = true;
        
        // 如果有图片和动作文件，加载它们
        if (this.images.length > 0 && !this.imagesLoaded) {
          this.loadImages();
        }
        
        if (this.actionFile && !this.actionLoaded) {
          this.loadActionFile();
        }
        
        // 如果图片和动作已加载，则调用处理方法
        if (this.imagesLoaded && this.actionLoaded) {
          this.processImages();
        }
      } else if (message instanceof ArrayBuffer) {
        // 获取处理后的图像数据
        const blob = new Blob([message], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        this.loading = false;
        this.$emit('process-complete', url);
      }
    },
    
    // 加载图片到Photopea
    loadImages() {
      if (!this.photopeaReady || this.images.length === 0) return;
      
      this.loading = true;
      
      // 首先打开第一张图片
      this.executeScript(`
        app.open("${this.images[0].url}", null, true);
      `);
      
      // 加载其他图片作为图层
      for (let i = 1; i < this.images.length; i++) {
        this.executeScript(`
          app.open("${this.images[i].url}", null, true);
          app.activeDocument.activeLayer.duplicate(app.documents[0]);
          app.documents[1].close();
        `);
      }
      
      // 标记图片已加载
      this.imagesLoaded = true;
      
      // 如果动作也已加载，则处理图像
      if (this.actionLoaded) {
        this.processImages();
      }
    },
    
    // 加载动作文件
    loadActionFile() {
      if (!this.photopeaReady || !this.actionFile) return;
      
      const actionUrl = `/actions/${this.actionFile}`;
      
      this.executeScript(`
        // 加载动作文件
        app.loadResource("${actionUrl}");
      `);
      
      // 标记动作已加载
      this.actionLoaded = true;
      
      // 如果图片也已加载，则处理图像
      if (this.imagesLoaded) {
        this.processImages();
      }
    },
    
    // 处理图像
    processImages() {
      if (!this.photopeaReady || !this.imagesLoaded || !this.actionLoaded) return;
      
      // 获取动作名称（不含扩展名）
      const actionName = this.actionFile.split('.')[0];
      
      this.executeScript(`
        // 执行动作
        try {
          app.doAction("${actionName}", "${actionName}");
          
          // 导出处理后的图像
          app.activeDocument.saveToOE("png");
        } catch(e) {
          app.echoToOE("Error: " + e.message);
        }
      `);
    },
    
    // 执行脚本
    executeScript(script) {
      if (!this.photopeaInstance) return;
      
      this.photopeaInstance.postMessage(script, "*");
    }
  }
}
</script>

<style scoped>
.photopea-container {
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
  border-radius: 10px;
}

.photopea-frame {
  width: 100%;
  height: 100%;
  border: none;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(5px);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #4361ee;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 10px;
}

.loading-text {
  color: white;
  font-size: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style> 